import Table from "../../components/list/Table";
import { format, parseISO } from "date-fns";
import { Card, Row, Col, Form, Button, Modal } from "react-bootstrap";
import {
  useGetTeamUserListQuery,
  useGetUserProfileQuery,
  useInviteTeamMemberMutation,
  useUpdateSettingsMutation,
} from "../../features/users/usersApi";
import { UserRole } from "../../features/users/usersTypes";
import { Navigate } from "react-router";
import FullScreenLoader from "../../components/FullScreenLoader";
import { toast } from "react-toastify";
import { useState } from "react";

const TeamManagement = () => {
  const { data, isFetching, refetch } = useGetTeamUserListQuery(undefined);
  const { data: userData, isFetching: isFetchingUser } = useGetUserProfileQuery(undefined);
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();
  const [inviteMember, { isLoading: isInviting }] = useInviteTeamMemberMutation();
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState<string>(UserRole.USER);

  const resetInviteForm = () => {
    setInviteEmail("");
    setInviteFirstName("");
    setInviteLastName("");
    setInviteRole(UserRole.USER);
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    const firstName = inviteFirstName.trim();
    if (!email) {
      toast.error("Enter an email address");
      return;
    }
    if (!firstName) {
      toast.error("Enter the invitee's first name");
      return;
    }
    try {
      await inviteMember({
        email,
        first_name: firstName,
        last_name: inviteLastName.trim() || undefined,
        role: inviteRole as "admin" | "user",
      }).unwrap();
      toast.success("Invitation sent. They will receive an email with a link to join.");
      setShowInviteModal(false);
      resetInviteForm();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to send invitation");
    }
  };

  const handleInviteAgain = async (teamUser: {
    email: string;
    first_name?: string;
    last_name?: string;
    full_name: string;
    role: string;
    user_id?: string;
  }) => {
    const id = teamUser.user_id || teamUser.email;
    setUpdatingUserId(id);
    try {
      const parts = (teamUser.full_name || "").trim().split(/\s+/);
      const first =
        (teamUser.first_name || "").trim() || parts[0] || "";
      const last =
        (teamUser.last_name || "").trim() ||
        (parts.length > 1 ? parts.slice(1).join(" ") : "");
      await inviteMember({
        email: teamUser.email.trim(),
        first_name: first,
        last_name: last || undefined,
        role: teamUser.role === UserRole.ADMIN ? "admin" : "user",
      }).unwrap();
      toast.success("Invitation sent again.");
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to resend invitation");
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleRoleChange = async (userId: string | undefined, email: string, newRole: string) => {
    if (!userId && !email) {
      toast.error("Unable to identify user");
      return;
    }

    setUpdatingUserId(userId || email);
    try {
      const updateData: any = { role: newRole };
      // If we have user_id, include it; otherwise backend might use email
      if (userId) {
        updateData.user_id = userId;
      } else {
        updateData.email = email;
      }

      await updateSettings(updateData).unwrap();
      toast.success("Role updated successfully");
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update role");
    } finally {
      setUpdatingUserId(null);
    }
  };

  const columns = [
    {
      id: "full_name",
      header: "Name",
      accessorKey: "full_name",
    },
    {
      id: "email",
      header: "Email",
      accessorKey: "email",
    },
    {
      id: "role",
      header: "Role",
      accessorKey: "role",
      cell: ({ row }: any) => {
        const teamUser = row.original;
        const role = teamUser.role;
        const currentUserEmail = userData?.data?.email;
        const isCurrentUser = teamUser.email === currentUserEmail || teamUser.user_id === userData?.data?.user_id;
        const isUpdating = updatingUserId === (teamUser.user_id || teamUser.email);

        if (teamUser.invitation_pending) {
          return (
            <span className="badge bg-light text-dark border">
              {role ? role.charAt(0).toUpperCase() + role.slice(1) : "N/A"}
            </span>
          );
        }

        if (isCurrentUser) {
          return (
            <span className="badge bg-primary-2">
              {role ? role.charAt(0).toUpperCase() + role.slice(1) : "N/A"}
            </span>
          );
        }

        return (
          <Form.Select
            value={role || ""}
            onChange={(e) => handleRoleChange(teamUser.user_id, teamUser.email, e.target.value)}
            disabled={isUpdating}
            size="sm"
            style={{ width: "auto", minWidth: "100px", display: "inline-block" }}
          >
            <option value={UserRole.USER}>Member</option>
            <option value={UserRole.ADMIN}>Admin</option>
          </Form.Select>
        );
      },
    },
    {
      id: "is_active",
      header: "Status",
      accessorKey: "is_active",
      cell: ({ row }: any) => {
        const teamUser = row.original;
        if (teamUser.invitation_pending) {
          return (
            <span className="badge bg-warning text-dark">Pending invite</span>
          );
        }
        const isActive = teamUser.is_active;
        return (
          <span
            className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      id: "invite_action",
      header: "",
      accessorKey: "invitation_pending",
      cell: ({ row }: any) => {
        const teamUser = row.original;
        if (!teamUser.invitation_pending) {
          return null;
        }
        const rowKey = teamUser.user_id || teamUser.email;
        const busy = isInviting && updatingUserId === rowKey;
        return (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleInviteAgain(teamUser)}
            disabled={busy}
          >
            {busy ? "Sending..." : "Invite again"}
          </Button>
        );
      },
    },
    {
      id: "created_at",
      header: "Added On",
      accessorKey: "created_at",
      cell: ({ row }: any) => {
        if (row.original.created_at) {
          return format(parseISO(row.original.created_at), "dd MMM, yyyy");
        }
        return "N/A";
      },
    },
  ];

  // Show loader while checking user role
  if (isFetchingUser) {
    return <FullScreenLoader />;
  }

  // Redirect to home if user is not admin
  if (userData?.data?.role !== UserRole.ADMIN) {
    return <Navigate to="/" replace />;
  }

  return (
    <Row>
      <Col>
        <Card style={{ border: "none", boxShadow: "none", borderLeft: "1px solid rgba(25, 34, 46, 0.08)", borderRadius: "0" }}>
          <Card.Body style={{ padding: 0, backgroundColor: "transparent" }}>
            <div
              className="sticky-filter-bar"
              style={{
                position: "sticky",
                top: "50px",
                zIndex: 100,
                height: "50px",
                marginBottom: 0,
                display: "flex",
                gap: "20px",
                padding: "10px 20px",
                lineHeight: "24px",
                borderBottom: "1px solid #eef0f2",
                alignItems: "center",
                justifyContent: "flex-end",
              }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowInviteModal(true)}
              >
                Add member
              </Button>
            </div>
            <Row>
              <Col className="m-1">
                <div className="table-wrapper">
                  <Table
                    columns={columns}
                    data={data?.data ?? []}
                    isSearchable={true}
                    isSortable={true}
                    isFetching={isFetching || isUpdating}
                    theadClass="table-light rounded sticky-table-header"
                  />
                </div>
                {!isFetching && !data?.data?.length && (
                  <div style={{ padding: "20px", paddingTop: "0", marginTop: "0", background: "#ffffff", borderTop: "1px solid #e9ecef" }}>
                    <p className="text-muted text-center italic">No Entry Found</p>
                  </div>
                )}

              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Modal
          show={showInviteModal}
          onHide={() => {
            setShowInviteModal(false);
            resetInviteForm();
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Invite a team member</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSendInvite}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>First name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Jane"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  autoFocus
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Last name (optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Doe"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value={UserRole.USER}>Member</option>
                  <option value={UserRole.ADMIN}>Admin</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Text className="text-muted">
                We will email them a secure link to create an account. If your account has a saved
                payment method, it will be shared with their profile when they join.
              </Form.Text>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => {
                  setShowInviteModal(false);
                  resetInviteForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isInviting}>
                {isInviting ? "Sending…" : "Send invite"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Col>
    </Row>
  );
};

export default TeamManagement;
